<template>
    <span
        v-if="regle"
        class="regle-pastille"
        :class="{ 'regle-pastille--mise-en-avant': estMiseEnAvant }"
        :data-regle-cle="cle"
    >
        <Popper arrow hover :placement="placement" class="regle-pastille__popper">
            <button
                type="button"
                class="regle-pastille__bouton"
                :aria-label="regle.titre"
                @click.stop
                @dblclick.stop
                @keydown.enter.stop
            >
                <svg aria-hidden="true" class="regle-pastille__icone" focusable="false" viewBox="0 0 24 24">
                    <path
                        fill="currentColor"
                        d="M12 21.5C10.65 20.65 8.2 20 6.5 20C4.85 20 3.15 20.3 1.75 21.05C1.65 21.1 1.6 21.1 1.5 21.1C1.25 21.1 1 20.85 1 20.6V6C1.6 5.55 2.25 5.25 3 5C4.11 4.65 5.33 4.5 6.5 4.5C8.45 4.5 10.55 4.9 12 6C13.45 4.9 15.55 4.5 17.5 4.5C18.67 4.5 19.89 4.65 21 5C21.75 5.25 22.4 5.55 23 6V20.6C23 20.85 22.75 21.1 22.5 21.1C22.4 21.1 22.35 21.1 22.25 21.05C20.85 20.3 19.15 20 17.5 20C15.8 20 13.35 20.65 12 21.5M12 8V19.5C13.35 18.65 15.8 18 17.5 18C18.7 18 19.9 18.15 21 18.5V7C19.9 6.65 18.7 6.5 17.5 6.5C15.8 6.5 13.35 7.15 12 8M13 11.5C14.11 10.82 15.6 10.5 17.5 10.5C18.41 10.5 19.26 10.59 20 10.78V9.23C19.13 9.08 18.29 9 17.5 9C15.73 9 14.23 9.28 13 9.84V11.5M17.5 11.67C15.79 11.67 14.29 11.93 13 12.46V14.15C14.11 13.5 15.6 13.16 17.5 13.16C18.54 13.16 19.38 13.24 20 13.4V11.9C19.13 11.74 18.29 11.67 17.5 11.67M20 14.57C19.13 14.41 18.29 14.33 17.5 14.33C15.67 14.33 14.17 14.6 13 15.13V16.82C14.11 16.16 15.6 15.83 17.5 15.83C18.54 15.83 19.38 15.91 20 16.07V14.57Z"
                    />
                </svg>
            </button>
            <template #content>
                <div class="regle-pastille__contenu">
                    <div class="regle-pastille__titre">{{ regle.titre }}</div>
                    <p class="regle-pastille__texte">{{ regle.texte }}</p>
                    <div class="regle-pastille__source">{{ regle.source }}</div>
                </div>
            </template>
        </Popper>
    </span>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { REGLES_PAR_CLE, RegleGestion } from '@/regles/reglesGestion';

export default defineComponent({
    name: 'ReglePastille',
    props: {
        cle: {
            type: String as PropType<string>,
            required: true,
        },
        // En-tête de tableau sticky : "top" serait rogné par le conteneur scrollable
        placement: {
            type: String as PropType<string>,
            required: false,
            default: 'top',
        },
    },
    computed: {
        regle(): RegleGestion | undefined {
            if (!this.$store.getters['reglesGestionState/isActif']) {
                return undefined;
            }
            return REGLES_PAR_CLE[this.cle];
        },
        estMiseEnAvant(): boolean {
            return this.$store.getters['reglesGestionState/getCleMiseEnAvant'] === this.cle;
        },
    },
});
</script>

<style scoped>
.regle-pastille {
    display: inline-flex;
    vertical-align: middle;
    margin-left: 6px;
}
:deep(.popper) {
    background-color: #fff;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
    border-radius: 4px;
    z-index: 1010;
}
:deep(.popper #arrow::before) {
    background: #fff;
}
.regle-pastille__bouton {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 1px solid var(--bleu-secondaire);
    background-color: var(--bleu-secondaire);
    color: #fff;
    line-height: 1;
    cursor: help;
    padding: 0;
}
.regle-pastille__icone {
    width: 12px;
    height: 12px;
}
.regle-pastille__contenu {
    max-width: 420px;
    border-left: 4px solid var(--bleu-secondaire);
    padding: 8px 10px;
    text-align: left;
    font-size: 13px;
    font-weight: normal;
    line-height: 1.4;
    background-color: #fff;
}
.regle-pastille__titre {
    font-size: 14px;
    font-weight: bold;
    color: var(--bleu-prinicipal);
    margin-bottom: 4px;
    line-height: 1.3;
}
.regle-pastille__texte {
    margin: 0 0 6px;
    font-size: 13px;
    font-weight: normal;
}
.regle-pastille__source {
    font-size: 11px;
    font-style: italic;
    font-weight: normal;
    color: #6b7280;
}
.regle-pastille--mise-en-avant .regle-pastille__bouton {
    animation: regle-pulse 1.2s ease-in-out 0s 3;
}
@keyframes regle-pulse {
    0% {
        box-shadow: 0 0 0 0 rgba(46, 77, 230, 0.7);
        transform: scale(1);
    }
    50% {
        box-shadow: 0 0 0 6px rgba(46, 77, 230, 0);
        transform: scale(1.3);
    }
    100% {
        box-shadow: 0 0 0 0 rgba(46, 77, 230, 0);
        transform: scale(1);
    }
}
</style>
